import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Check, X, Package } from 'lucide-react';
import ApiService from '../services/ApiService';

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', unitPrice: '', category: '', unit: '' });
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await ApiService.apiCall('/product?includeInactive=true');
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = async () => {
    if (!form.name.trim()) return;
    try {
      setSaving(true);
      const created = await ApiService.apiCall('/product', {
        method: 'POST',
                body: JSON.stringify({
          name: form.name.trim(),
          description: form.description.trim() || null,
          unitPrice: parseFloat(form.unitPrice) || 0,
          category: form.category.trim() || null,
          unit: form.unit.trim() || null
        })
      });
      setProducts(prev => [...prev, created]);
      setForm({ name: '', description: '', unitPrice: '', category: '', unit: '' });
      setShowAdd(false);
    } catch (err) {
      console.error('Error creating product:', err);
      alert('Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({ name: p.name, description: p.description || '', unitPrice: String(p.unitPrice), category: p.category || '', unit: p.unit || '', isActive: p.isActive });
  };

  const saveEdit = async () => {
    try {
      setSaving(true);
      const updated = await ApiService.apiCall(`/product/${editingId}`, {
        method: 'PUT',
                body: JSON.stringify({
          name: editForm.name.trim(),
          description: editForm.description.trim() || null,
          unitPrice: parseFloat(editForm.unitPrice) || 0,
          category: editForm.category.trim() || null,
          unit: editForm.unit.trim() || null,
          isActive: editForm.isActive
        })
      });
      setProducts(prev => prev.map(p => p.id === editingId ? updated : p));
      setEditingId(null);
    } catch (err) {
      console.error('Error updating product:', err);
      alert('Failed to update product.');
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await ApiService.apiCall(`/product/${id}`, { method: 'DELETE' });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Failed to delete product.');
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm";

  if (loading) return <div className="text-sm text-gray-400 py-4">Loading products...</div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Products &amp; Services</h3>
          <p className="text-sm text-gray-500 mt-0.5">Manage reusable items for the work order price calculator.</p>
        </div>
        <button
          onClick={() => setShowAdd(v => !v)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-sm flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </div>

      {showAdd && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 space-y-3">
          <h4 className="font-medium text-blue-800">New Product / Service</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
              <input type="text" placeholder="e.g. Inspection" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unit Price *</label>
              <input type="number" step="0.01" placeholder="0.00" value={form.unitPrice} onChange={e => setForm(p => ({ ...p, unitPrice: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <input type="text" placeholder="e.g. Services, Materials" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unit</label>
              <input type="text" placeholder="e.g. ea, hr, sqft, day" value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} className={inputClass} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <input type="text" placeholder="Optional description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={addProduct} disabled={!form.name.trim() || saving} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </div>
      )}

      {products.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No products yet. Add products to use in the work order calculator.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.id} className={`flex items-center gap-3 bg-white border rounded-lg p-3 ${!p.isActive ? 'opacity-50' : 'border-gray-200'}`}>
              {editingId === p.id ? (
                <>
                  <div className="flex-1 grid grid-cols-5 gap-2">
                    <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="px-2 py-1 border rounded text-sm col-span-1" placeholder="Name" />
                    <input value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} className="px-2 py-1 border rounded text-sm col-span-1" placeholder="Description" />
                    <input value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} className="px-2 py-1 border rounded text-sm" placeholder="Category" />
                    <input value={editForm.unit} onChange={e => setEditForm(f => ({ ...f, unit: e.target.value }))} className="px-2 py-1 border rounded text-sm" placeholder="Unit" />
                    <input type="number" step="0.01" value={editForm.unitPrice} onChange={e => setEditForm(f => ({ ...f, unitPrice: e.target.value }))} className="px-2 py-1 border rounded text-sm" placeholder="Price" />
                  </div>
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <input type="checkbox" checked={editForm.isActive} onChange={e => setEditForm(f => ({ ...f, isActive: e.target.checked }))} />
                    Active
                  </label>
                  <button onClick={saveEdit} disabled={saving} className="text-green-500 hover:text-green-700"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{p.name}</span>
                      {p.category && <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{p.category}</span>}
                      {p.unit && <span className="text-xs text-gray-400">/ {p.unit}</span>}
                      {!p.isActive && <span className="text-xs bg-red-100 text-red-500 px-1.5 py-0.5 rounded">Inactive</span>}
                    </div>
                    {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
                  </div>
                  <span className="text-sm font-semibold text-green-700">${(p.unitPrice || 0).toFixed(2)}</span>
                  <button onClick={() => startEdit(p)} className="text-blue-400 hover:text-blue-600"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => removeProduct(p.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsManager;
